<template>
  <div class="step-service">
    <h2 class="step-service__title">Qual é o serviço que precisa?</h2>

    <div class="step-service__search p-inputgroup mb-4">
      <span class="p-inputgroup-addon"><i class="pi pi-search" /></span>
      <InputText
        v-model="searchQuery"
        placeholder="Pesquise por nome, tema ou palavras dos textos de ajuda..."
        @input="onSearchInput"
      />
      <Button
        v-if="searchQuery"
        icon="pi pi-times"
        class="p-button-secondary"
        @click="clearSearch"
      />
    </div>

    <div class="step-service__families mb-4">
      <button
        v-for="family in families"
        :key="family.value"
        type="button"
        class="family-chip"
        :class="{ 'family-chip--active': selectedFamily === family.value }"
        @click="toggleFamily(family.value)"
      >
        <i :class="family.icon" />
        <span>{{ family.label }}</span>
      </button>
    </div>

    <div v-if="isLoading" class="step-service__grid">
      <div v-for="n in 6" :key="n" class="service-card service-card--skeleton">
        <div class="skeleton skeleton--title" />
        <div class="skeleton skeleton--text" />
        <div class="skeleton skeleton--text skeleton--short" />
      </div>
    </div>

    <div v-else-if="services.length === 0" class="step-service__empty">
      <i class="pi pi-inbox" />
      <p>Nenhum serviço disponível para o seu perfil.</p>
      <p v-if="selectedFamily || searchQuery">
        <button
          type="button"
          class="p-button p-button-link"
          @click="resetFilters"
        >
          Limpar filtros
        </button>
      </p>
    </div>

    <div v-else class="step-service__grid">
      <ServiceCard
        v-for="service in services"
        :key="service.id"
        :service="service"
        @select="onSelectService"
      />
    </div>
  </div>
</template>

<script>
import ServiceCard from "./ServiceCard.vue";

const FAMILIES = [
  { label: "Fiscal", value: "Fiscal", icon: "pi pi-file" },
  { label: "Administrativo", value: "Administrativo", icon: "pi pi-briefcase" },
  { label: "Migratório", value: "Migratório", icon: "pi pi-globe" },
  { label: "Imobiliário", value: "Imobiliário", icon: "pi pi-home" },
  { label: "Seguros", value: "Seguros", icon: "pi pi-shield" },
  { label: "Previdência", value: "Previdência", icon: "pi pi-heart" },
  { label: "Crédito", value: "Crédito", icon: "pi pi-credit-card" },
];

export default {
  name: "AppointmentStepService",
  components: { ServiceCard },
  props: {
    isFromActivation: {
      type: Boolean,
      default: false,
    },
    catalogCountry: {
      type: String,
      default: null,
    },
    country: {
      type: String,
      default: null,
    },
  },
  emits: ["select", "services-loaded"],
  data() {
    return {
      services: [],
      isLoading: false,
      searchQuery: "",
      selectedFamily: null,
      searchTimeout: null,
      families: FAMILIES,
    };
  },
  watch: {
    catalogCountry() {
      this.fetchServices();
    },
  },
  created() {
    this.fetchServices();
  },
  methods: {
    // O desconto Balcão Mais vem calculado do servidor (cartão activo, prazo e promoções já aplicados),
    // o mesmo valor que depois segue para o link Stripe e para os emails.
    getCardPricing(service) {
      const percent = this.parseNumber(service.cardDiscountPercent);
      const price = this.parseNumber(service.priceWithCard);
      if (percent === null || price === null) return null;
      return { percent, price };
    },
    parsePromotionsFromService(service) {
      // As entradas com cardTypeId são descontos de cartão, não promoções por datas
      if (Array.isArray(service.promotions))
        return service.promotions.filter((p) => !p.cardTypeId);
      if (
        !service.servicePricePromotions ||
        typeof service.servicePricePromotions !== "string"
      )
        return [];
      return service.servicePricePromotions
        .split("||")
        .map((entry) => {
          const parts = String(entry || "").split("/");
          if (parts.length < 6) return null;
          return {
            id: parts[0] ? Number(parts[0]) : null,
            discount: parts[1] || null,
            description: parts[2] || null,
            price: parts[3] || null,
            startDate: parts[4] || null,
            finishDate: parts[5] || null,
            // Quando não vem explícito, assumimos visível na área cliente.
            serviceInClientArea: true,
          };
        })
        .filter((p) => !!p);
    },
    parseNumber(value) {
      if (value === null || value === undefined || value === "") return null;
      const normalized = String(value).replace("%", "").trim();
      const n = parseFloat(normalized);
      return isNaN(n) ? null : n;
    },
    getBasePriceForDisplay(service) {
      const withIva = this.parseNumber(service.priceWithIva);
      if (withIva !== null) return withIva;
      return this.parseNumber(service.price);
    },
    isPromotionActiveForClientArea(promotion) {
      if (!promotion || promotion.serviceInClientArea === false) return false;
      const now = new Date();
      const start = promotion.startDate ? new Date(promotion.startDate) : null;
      const finish = promotion.finishDate
        ? new Date(promotion.finishDate)
        : null;
      if (finish && String(promotion.finishDate).length <= 10) {
        finish.setHours(23, 59, 59, 999);
      }
      if (start && now < start) return false;
      if (finish && now > finish) return false;
      return true;
    },
    getPromotionCandidateValue(service, promotion) {
      const baseDisplay = this.getBasePriceForDisplay(service);
      if (baseDisplay === null) return null;
      const candidates = [];
      const promotionPrice = this.parseNumber(promotion && promotion.price);
      const discount = this.parseNumber(promotion && promotion.discount);
      if (promotionPrice !== null) {
        const baseRaw = this.parseNumber(service.price);
        const baseWithIva = this.parseNumber(service.priceWithIva);
        const multiplier =
          baseRaw && baseWithIva ? baseWithIva / baseRaw : null;
        candidates.push(
          multiplier ? promotionPrice * multiplier : promotionPrice
        );
      }
      if (discount !== null) {
        candidates.push(baseDisplay * (1 - discount / 100));
      }
      if (!candidates.length) return null;
      return Math.max.apply(null, candidates);
    },
    enrichServiceWithPromotion(service) {
      const basePrice = this.getBasePriceForDisplay(service);
      const cardPricing = this.getCardPricing(service);
      if (basePrice !== null && cardPricing !== null) {
        return {
          ...service,
          priceToShow: cardPricing.price,
          oldPriceToShow: basePrice,
          discountPercent: cardPricing.percent,
          pricingSource: "card",
          hasPromotionApplied: false,
          promotionDiscount: null,
        };
      }

      const promotions = this.parsePromotionsFromService(service);
      const activePromotions = promotions.filter((p) =>
        this.isPromotionActiveForClientArea(p)
      );
      let bestPromotionValue = null;
      let bestPromotion = null;
      activePromotions.forEach((p) => {
        const val = this.getPromotionCandidateValue(service, p);
        if (val === null) return;
        if (bestPromotionValue === null || val > bestPromotionValue) {
          bestPromotionValue = val;
          bestPromotion = p;
        }
      });
      return {
        ...service,
        promotions,
        promotionPrice: bestPromotionValue,
        priceToShow:
          bestPromotionValue !== null ? bestPromotionValue : basePrice,
        hasPromotionApplied: bestPromotionValue !== null,
        promotionDiscount: bestPromotion
          ? this.parseNumber(bestPromotion.discount)
          : null,
        oldPriceToShow: bestPromotionValue !== null ? basePrice : null,
        discountPercent: bestPromotion
          ? this.parseNumber(bestPromotion.discount)
          : null,
        pricingSource: bestPromotionValue !== null ? "promotion" : null,
      };
    },
    clientCatalogCountry() {
      const fromCatalog = this.catalogCountry
        ? String(this.catalogCountry).toUpperCase()
        : null;
      if (fromCatalog) return fromCatalog;
      if (this.isFromActivation) return null;
      const country = this.country
        ? this.country
        : this.$root.user
        ? this.$root.user.country
        : null;
      if (!country || country === "PT") return "PT";
      return String(country).toUpperCase();
    },
    buildParams() {
      const params = {
        acceptAppointments: true,
        serviceInClientArea: true,
        isActive: true,
        state: "active",
      };
      const catalog = this.clientCatalogCountry();
      if (catalog) {
        params.country = catalog;
      } else {
        const country = this.country
          ? this.country
          : this.$root.user
          ? this.$root.user.country
          : null;
        params.country =
          !country || country === "PT"
            ? "PT"
            : [String(country).toUpperCase(), "PT"];
      }
      if (this.selectedFamily) params.family = this.selectedFamily;
      if (this.searchQuery.trim())
        params.appointmentSearch = this.searchQuery.trim();
      return params;
    },
    fetchServices() {
      this.isLoading = true;
      this.$root
        .apiGet(
          this.isFromActivation
            ? this.$root.api.servicePricesUnauth
            : this.$root.api.servicePrices,
          { params: this.buildParams() }
        )
        .then((response) => {
          const normalized = (response.data.data || []).map((service) =>
            this.enrichServiceWithPromotion(service)
          );
          this.services = this.deduplicateAndSortByLowestPrice(normalized);
          this.$emit("services-loaded", this.services);
        })
        .catch((error) => {
          this.$root.toastError(
            (error &&
              error.response &&
              error.response.data &&
              error.response.data.msg_code) ||
              "error"
          );
          this.services = [];
          this.$emit("services-loaded", []);
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    deduplicateAndSortByLowestPrice(services) {
      // Para o mesmo service_id, mantém o registo com o preço mais baixo
      const map = new Map();
      for (const s of services) {
        const key = s.serviceId || s.id;
        const existing = map.get(key);
        if (!existing) {
          map.set(key, s);
        } else {
          const existingRaw =
            existing.priceToShow !== undefined && existing.priceToShow !== null
              ? existing.priceToShow
              : this.getBasePriceForDisplay(existing);
          const newRaw =
            s.priceToShow !== undefined && s.priceToShow !== null
              ? s.priceToShow
              : this.getBasePriceForDisplay(s);
          const existingPrice =
            existingRaw !== null ? parseFloat(existingRaw) : Infinity;
          const newPrice = newRaw !== null ? parseFloat(newRaw) : Infinity;
          if (newPrice < existingPrice) map.set(key, s);
        }
      }
      return Array.from(map.values());
    },
    onSearchInput() {
      clearTimeout(this.searchTimeout);
      this.selectedFamily = null;
      this.searchTimeout = setTimeout(() => this.fetchServices(), 400);
    },
    clearSearch() {
      this.searchQuery = "";
      this.fetchServices();
    },
    toggleFamily(family) {
      this.selectedFamily = this.selectedFamily === family ? null : family;
      this.searchQuery = "";
      this.fetchServices();
    },
    resetFilters() {
      this.selectedFamily = null;
      this.searchQuery = "";
      this.fetchServices();
    },
    onSelectService(service) {
      this.$emit("select", service);
    },
  },
};
</script>
